import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';

import { ChangeDetectionStrategy, Component, EventEmitter, Output, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { OutsideClickDirective } from 'projects/components/directive/outsideclick.directive';
import { TryGetLangFromStorage } from 'projects/components/language-selector/try-get-lang-from.storage';
import { IconComponent } from '../pay-icon/icon.component';
import { IconNames } from '../pay-icon/IconNames';
import { LanguageSelectorItem } from './models/language-selector-item';

@Component({
  selector: 'pay-lib-language-selector',
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, OutsideClickDirective, IconComponent],
  templateUrl: './language-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSelectorComponent {
  _translateService = inject(TranslateService);
  private readonly _title = inject(Title);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);

  @Output() selectedLanguageChange = new EventEmitter<string>();

  languages: LanguageSelectorItem[] = new Array<LanguageSelectorItem>(
    {
      displayName: 'Nederlands',
      abbreviation: 'NL',
      locale: 'nl_NL',
    },
    {
      displayName: 'Deutsch',
      abbreviation: 'DE',
      locale: 'de_DE',
    },
    {
      displayName: 'Français',
      abbreviation: 'FR',
      locale: 'fr_FR',
    },
    {
      displayName: 'English',
      abbreviation: 'EN',
      locale: 'en_GB',
    },
  );

  selectedLanguage = this.languages[0];
  isOpen: boolean = false;

  protected readonly IconNames = IconNames;

  constructor() {
    // Initial set of language
    const localStorageLang = TryGetLangFromStorage();

    this.selectedLanguage = this.languages.find((x) => x.abbreviation.toLowerCase() === localStorageLang) ?? this.languages[0];
    if (this._translateService.currentLang === undefined) {
      this.selectLanguage(this.selectedLanguage);
    }

    this._translateService.onLangChange.subscribe((event) => {
      this.selectedLanguage = this.languages.find((x) => x.abbreviation.toLowerCase() === event.lang) ?? this.languages[0];
      this.setPageTitle();
    });
  }

  selectLanguage(language: LanguageSelectorItem) {
    const lowercaseLang = language.abbreviation.toLowerCase();
    this._translateService.use(lowercaseLang);
    this.selectedLanguage = language;
    localStorage.setItem('lang', lowercaseLang);

    this.isOpen = false;
  }

  // Uses the active route to find the appropriate page title when the language selection has changed
  setPageTitle() {
    const pathInfo = this._activatedRoute.snapshot.routeConfig?.children?.map((x) =>
      x.children?.find((y) => this._router.routerState.snapshot.url.includes(y.path ?? '')),
    );

    if (pathInfo && pathInfo[0] && pathInfo[0].title && typeof pathInfo[0].title === 'string') {
      const titleTranslation = pathInfo[0].title;
      this._translateService.get(titleTranslation).subscribe({
        next: (translatedTitle) => {
          this._title.setTitle(translatedTitle);
        },
      });
    }
  }
}
